import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {
    Typography,
    Button,
    Modal,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputAdornment,
    Avatar,
    Chip,
    IconButton,
} from '@mui/material'
import {Search, Delete, Close} from '@mui/icons-material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {caregiverApi} from '../../../../services'
import {getInitials, getColorFromName, parseError} from '../../../../utils/helpers'
import {useParams} from 'react-router-dom'
import {useMobile} from '../../../../hooks'

export default ({open, setOpen, loadData, caregiver, allClients, caregiverClients}) => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const [clients, setClients] = useState([])
    const [services, setServices] = useState([])
    const [availableClients, setAvailableClients] = useState([])
    const [selectedClient, setSelectedClient] = useState(null)
    const [searchClientTerm, setSearchClientTerm] = useState('')
    const filteredClients = clients?.filter(item => item?.display_name?.toLowerCase().includes(searchClientTerm?.toLowerCase()))
    const [selectedServices, setSelectedServices] = useState([])
    const [searchServiceTerm, setSearchServiceTerm] = useState('')
    const filteredServices = services?.filter(item => item?.display_name?.toLowerCase().includes(searchServiceTerm?.toLowerCase()))
    const [assignedClients, setAssignedClients] = useState([])

    useEffect(() => {
        setSelectedClient(null)
        setServices([])
        setSelectedServices([])
        setAssignedClients([])
        setAvailableClients(allClients?.filter(item => !(caregiverClients ?? []).some(item2 => item2.client_id === item.id)))
    }, [open])

    useEffect(() => {
        setClients(availableClients?.map(item => ({...item, display_name: item?.first_name + ' ' + item?.last_name})))
    }, [availableClients])

    const clientChange = event => {
        setSelectedClient(event.target.value)
        setSelectedServices([])
        const client = availableClients?.find(item => item?.id == event.target.value)
        if (client?.services) {
            const availableServices = client?.services?.filter(item =>
                (caregiver?.services ?? []).some(item2 => item2.service_id === item.service_id),
            )
            setServices(availableServices?.map(item => ({...item, id: item?.service_id, display_name: item?.service_name})))
        }
    }

    const renderClientLabel = selected => {
        const client = clients.find(item => item.id === selected)
        return client ? client.display_name : 'Select a client'
    }

    const serviceChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            if (selectedServices.length == services.length) {
                setSelectedServices([])
            } else {
                setSelectedServices(services?.map(item => item.id))
            }
        } else {
            setSelectedServices(value)
        }
    }

    const renderServiceLabel = selecteds => {
        if (selecteds.length == 0) {
            return 'Select services'
        }
        if (services.length == selecteds.length) {
            return 'All Services'
        }
        return services
            .filter(item => selecteds.includes(item.id))
            .map(item => item.display_name)
            .join(', ')
    }

    const submit = async () => {
        if (assignedClients?.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Client Selected!',
                description: 'Please select a client.',
            })
            return
        }

        try {
            setLoading(true)
            for (const assignedClient of assignedClients) {
                await caregiverApi.createCaregiverClients(branchId, caregiverId, assignedClient?.value)
            }
            setLoading(false)
            setOpen(false)
            setMessageBox({
                status: 'success',
                title: 'Client Assigned!',
                description: `${caregiver?.first_name} has been assigned to new clients.`,
            })
            loadData()
        } catch (error) {
            setLoading(false)
            setOpen(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    const addItem = () => {
        if (selectedClient?.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Client Selected!',
                description: 'Please select a client.',
            })
            return
        }

        if (selectedServices.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Service Selected!',
                description: 'Please select a service.',
            })
            return
        }

        const temp = []
        for (const selectedService of selectedServices) {
            temp.push(services?.find(item => item?.id == selectedService))
        }

        const newItem = {
            value: {
                client_id: selectedClient,
                service_ids: selectedServices,
            },
            display: {
                client: clients?.find(item => item?.id == selectedClient),
                services: temp,
            },
        }

        setAssignedClients(prev => [...prev, {...newItem, id: Date.now()}])

        setSelectedClient(null)
        setServices([])
        setSelectedServices([])
        setAvailableClients(prev => prev.filter(item => item.id !== selectedClient))
    }

    const removeItem = item => {
        setAssignedClients(prev => prev.filter(item2 => item2.id !== item?.id))

        setSelectedClient(null)
        setServices([])
        setSelectedServices([])
        setAvailableClients(prev => [...prev, item?.display?.client])
    }

    const reset = () => {
        setSelectedClient(null)
        setServices([])
        setSelectedServices([])
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Assign Client
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={submit}>
                                    Assign Client
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Assign Client
                        </Typography>
                    )}
                    <br />
                    <div className="select-card">
                        <Typography variant="inputLabel" color="text.secondary">
                            Client
                        </Typography>
                        <FormControl fullWidth style={{marginBottom: 6}}>
                            <Select
                                value={selectedClient}
                                onChange={clientChange}
                                renderValue={renderClientLabel}
                                displayEmpty
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                            overflow: 'auto',
                                        },
                                    },
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        placeholder="Search client name..."
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={searchClientTerm}
                                        onChange={e => setSearchClientTerm(e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </MenuItem>
                                {filteredClients.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <div className="people-card">
                                            {item?.avatar_url ? (
                                                <Avatar src={item?.avatar_url} style={{width: 32, height: 32}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.first_name + ' ' + item?.last_name),
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 16,
                                                    }}>
                                                    {getInitials(item?.first_name + ' ' + item?.last_name)}
                                                </Avatar>
                                            )}
                                            <Typography variant="body2" color="text.primary">
                                                {item?.first_name + ' ' + item?.last_name}
                                            </Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <br />
                        <Typography variant="inputLabel" color="text.secondary">
                            Service
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                multiple
                                value={selectedServices}
                                onChange={serviceChange}
                                renderValue={renderServiceLabel}
                                displayEmpty
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                            overflow: 'auto',
                                        },
                                    },
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        placeholder="Search service name..."
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={searchServiceTerm}
                                        onChange={e => setSearchServiceTerm(e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </MenuItem>
                                {services?.length > 0 && (
                                    <MenuItem value="all">
                                        <div className="item">
                                            <Checkbox
                                                checked={selectedServices.length === services.length}
                                                indeterminate={selectedServices.length > 0 && selectedServices.length < services.length}
                                            />
                                            <ListItemText primary="Select All" />
                                        </div>
                                    </MenuItem>
                                )}
                                {filteredServices.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <div className="item">
                                            <Checkbox checked={selectedServices.includes(item.id)} />
                                            <b>
                                                <Chip
                                                    label={item?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                                                    color="action.selected"
                                                    variant="filled"
                                                    size="small"
                                                    style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                                />
                                            </b>
                                            <ListItemText primary={item.display_name} />
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <br />
                        <div className="add-actions">
                            <Button variant="outlined" size="small" style={{width: 92}} onClick={reset}>
                                Reset
                            </Button>
                            <Button variant="contained" size="small" style={{width: 92}} onClick={addItem}>
                                Save
                            </Button>
                        </div>
                    </div>
                    {assignedClients?.map((item, index) => (
                        <div key={index}>
                            <br />
                            <div className="assigned-card">
                                <div className="assigned-top">
                                    <div className="people-card">
                                        {item?.display?.client?.avatar_url ? (
                                            <Avatar src={item?.display?.client?.avatar_url} style={{width: 40, height: 40}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(
                                                        item?.display?.client?.first_name + ' ' + item?.display?.client?.last_name,
                                                    ),
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 20,
                                                }}>
                                                {getInitials(item?.display?.client?.first_name + ' ' + item?.display?.client?.last_name)}
                                            </Avatar>
                                        )}
                                        <Typography variant="h7" color="text.primary">
                                            {item?.display?.client?.first_name + ' ' + item?.display?.client?.last_name}
                                        </Typography>
                                    </div>
                                    <IconButton onClick={() => removeItem(item)}>
                                        <Delete color="error" />
                                    </IconButton>
                                </div>
                                <div className="assigned-content">
                                    <Typography variant="h9" color="text.secondary" style={{minWidth: 120, paddingTop: 6}}>
                                        Assigned Service:
                                    </Typography>
                                    <div className="services-content">
                                        {item?.display?.services?.map((item2, index2) => (
                                            <Chip label={item2?.service_name} color="action.selected" variant="filled" key={index2} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={submit}>
                                Assign Client
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
